import { useState } from 'react';

import { Box } from '@aicon-dev/caas-ui';
import { message } from 'antd';
import { NoticeBar } from 'antd-mobile';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ImagePinchZoom } from '../ImagePinchZoom';
import { UploadFileResponse } from '../Upload';

import { ForgeViewer } from './components/ForgeViewer';
import { getForgeviewerToken } from './components/ForgeViewer/libs/utils';
import PdfViewer from './components/PdfViewer';

export interface FileViewerProps {
  files?: UploadFileResponse[];
  /**
   * 각 뷰어 위에 다운로드 버튼을 노출합니다.
   */
  download?: boolean;
}

export const FileViewer = (props: FileViewerProps) => {
  const { files, download } = props;
  const { t } = useTranslation('report');

  // forgeViewer
  const [forgeView, setForgeView] = useState(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDocumentLoaded = (doc: any, viewables: any) => {
    if (viewables.length === 0) {
      console.error('Document contains no viewables.');
    } else {
      if (doc && doc.myData && doc.myData.status === 'inprogress') {
        message.error(doc.myData.progress, 10);
      } else {
        setForgeView(viewables[0]);
      }
    }
  };

  const handleModelError = () => {
    console.error('Error loading the model.');
  };

  const renderFile = (file: UploadFileResponse) => {
    const filename = file?.name || file.filename;
    if (filename?.match(/.(pdf|csv)$/i)) {
      return <PdfViewer url={file.url} style={{ padding: 0, marginBottom: 20 }} />;
    } else if (filename?.match(/.(jpg|jpeg|png|gif)$/i)) {
      return <ImagePinchZoom image={file} imageStyle={{ width: '100%', marginBottom: 20 }} />;
    } else if (filename?.match(/.(xlsx|xls|ppt|pptx|doc|docx)$/i)) {
      return (
        <iframe
          style={{
            height: '50vh',
            width: '100%',
            marginBottom: '20px',
          }}
          src={`https://view.officeapps.live.com/op/embed.aspx?src=${file.url}`}
        />
      );
    } else if (filename?.match(/.(mp4|avi)$/i)) {
      return (
        <div style={{ marginBottom: 20 }}>
          <video src={file.url} loop controls width="100%" height="100%" />
        </div>
      );
    } else if (filename?.match(/.(rvt|dwg|skp)$/i)) {
      return (
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '40vh',
            marginBottom: 20,
            overflow: 'hidden',
          }}
        >
          {file?.urn ? (
            <ForgeViewer
              version="7.61"
              urn={file.urn}
              headless={false}
              view={forgeView}
              onTokenRequest={getForgeviewerToken}
              onDocumentLoad={handleDocumentLoaded}
              onModelError={handleModelError}
            />
          ) : (
            <NoticeBar content={t('report:m_cannot_be_used_viewer')} color="default" />
          )}
        </div>
      );
    }
  };

  return (
    <Box>
      {files?.length !== 0 &&
        files?.map((file) => {
          return (
            <div key={file._id}>
              {download && (
                <FileDownload>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M17.4793 9.532L10.7911 16.2203C9.97179 17.0397 8.86052 17.5 7.70178 17.5C6.54305 17.5 5.43177 17.0397 4.61242 16.2203C3.79307 15.4009 3.33276 14.2896 3.33276 13.1309C3.33276 11.9721 3.79307 10.8608 4.61242 10.0414L11.3006 3.35312C11.8468 2.80688 12.5877 2.5 13.3602 2.5C14.1326 2.5 14.8735 2.80688 15.4197 3.35312C15.966 3.89937 16.2728 4.64024 16.2728 5.41275C16.2728 6.18526 15.966 6.92613 15.4197 7.47237L8.72429 14.1607C8.45118 14.4338 8.08075 14.5873 7.6945 14.5873C7.30826 14.5873 6.93783 14.4338 6.66472 14.1607C6.3916 13.8876 6.23816 13.5171 6.23816 13.1309C6.23816 12.7446 6.3916 12.3742 6.66472 12.1011L12.8434 5.92947"
                      stroke="#404040"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <FileName fileLinkExist={!!file.url} href={file.url}>
                    {file.filename || file.name || t('global:wrong_file')}
                  </FileName>
                </FileDownload>
              )}
              {renderFile(file)}
            </div>
          );
        })}
    </Box>
  );
};

const FileDownload = styled.div`
  display: flex;
  align-items: center;
  padding: 0 8px 0 12px;
  height: 40px;
  margin: 0 0 8px;

  color: #00000073;
  font-size: 1rem;
  font-weight: 400;

  border-radius: 6px;
  background: #fafafa;

  /* transition: background-color 0.2s ease; */
  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
`;

const FileName = styled.a<{ fileLinkExist: boolean }>`
  flex: 1;
  padding: 0 8px;
  color: rgba(0 0 0 / 50%);
  text-decoration: none;
  transition: none;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${({ fileLinkExist }) =>
    !!fileLinkExist &&
    `
    cursor: pointer;
    color: #505050;
  `}
`;
